import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useQuery } from "convex/react";
import { PageHeader } from "../PageHeader";
import { QueueTable } from "../shared/QueueTable";
import { refusalReason } from "../shared/refusalReason";
import {
  securityRoomStore,
  selectApprovalsList,
  approveItem,
  denyItem,
} from "../../../stores/securityRoomStore";
import { api as convexApi } from "../../../lib/convexApi";

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

// Ids with a decision in flight. A second press on the same row (a
// double-click, or the key and the button) would reach Convex after the first
// resolved it and be reported as a failure of a decision that went through.
const deciding = new Set<string>();

// Backend /api/approvals/:id/decision requires a non-empty `note` when
// denying, so a deny prompts for one and Cancel aborts it. A refusal is
// reported through `report` instead of being dropped.
async function decide(
  id: string,
  decision: "approve" | "deny",
  report: (message: string | null) => void
): Promise<void> {
  if (deciding.has(id)) return;
  let note: string | undefined;
  if (decision === "deny") {
    note = window.prompt("Reason for denying this approval?")?.trim();
    if (!note) return;
  }
  deciding.add(id);
  report(null);
  try {
    if (decision === "approve") await approveItem(id);
    else await denyItem(id, note);
  } catch (err) {
    report(`Couldn't ${decision}: ${refusalReason(err)}`);
  } finally {
    deciding.delete(id);
  }
}

export function ApprovalsPage() {
  const snap = securityRoomStore.useStore();
  const items = selectApprovalsList(snap);
  const [selected, setSelected] = useState(0);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  // Reactive Convex subscription for the pending approvals queue. When
  // Convex returns data, write it through to securityRoomStore so the
  // existing render path and selectors see live updates as approvals are
  // created / resolved. Falls back gracefully when Convex returns
  // undefined (loading or stubbed in tests) — the legacy SSE stream
  // continues to populate securityRoomStore.approvals.
  // Per [[feedback-pr46-review-patterns]] #5 we consume the actual
  // Convex shape (_id, command, requestedBy, createdAt, status, context).
  const convexPending = useQuery(convexApi.approvals.queries.pending, {});
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!mountedRef.current) return;
    if (convexPending === undefined) return;
    // Map Convex row → securityRoomStore approval shape (keeps the
    // existing selectApprovalsList view stable).
    const mapped = (convexPending as any[]).map((r) => ({
      id: r._id as string,
      status: r.status as string,
      recordedAt: new Date(r.createdAt as number).toISOString(),
      createdAt: new Date(r.createdAt as number).toISOString(),
      agent: (r.context && r.context.agent) || "",
      repo: (r.context && r.context.repo) || "",
      command: r.command as string,
      cmd: r.command as string,
      rule: (r.context && r.context.rule) || "",
      requestedBy: r.requestedBy as string,
    }));
    // Update BOTH `pendingApprovals` AND `approvals`. selectApprovalsList
    // (in securityRoomStore.ts:196) PREFERS `pendingApprovals` when set,
    // so writing only to `approvals` left the page rendering stale data
    // in any session where the legacy feed had previously populated
    // pendingApprovals (Codex PR #58 C58-4). Writing both keeps both
    // selector branches consistent.
    securityRoomStore.setState((current) => ({
      ...current,
      pendingApprovals: mapped,
      approvals: mapped,
    }));
  }, [convexPending]);

  useEffect(() => {
    if (selected >= items.length) {
      setSelected(Math.max(0, items.length - 1));
    }
  }, [items.length, selected]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (items.length === 0) return;
      // Don't hijack single-letter shortcuts while the user is typing in an
      // input/textarea/contenteditable, or holding a modifier (so browser
      // shortcuts like Cmd-A still work).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) {
        return;
      }
      // Holding a or d would decide row after row as each one resolves and
      // the selection moves on. j/k may repeat; a decision takes one press.
      if (e.repeat && (e.key === "a" || e.key === "d")) return;
      const current = items[selected] as Record<string, unknown> | undefined;
      const currentId = current && typeof current.id === "string" ? current.id : undefined;
      switch (e.key) {
        case "j":
          setSelected((i) => Math.min(items.length - 1, i + 1));
          break;
        case "k":
          setSelected((i) => Math.max(0, i - 1));
          break;
        case "a":
          if (currentId) void decide(currentId, "approve", setDecisionError);
          break;
        case "d":
          if (currentId) void decide(currentId, "deny", setDecisionError);
          break;
        // 's' (snooze) intentionally not wired in v1 — there is no
        // server-side snooze API yet, and a visual-only stub on a
        // security-triage queue produced a false sense of progress
        // (items "snoozed" stayed pending). Re-add when the backend
        // ships ephemeral snooze state.
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, selected]);

  return (
    <>
      <PageHeader
        title="Approvals"
        subtitle={`${items.length} pending`}
        actions={
          <button
            type="button"
            className="sr-btn sr-btn--ghost"
            onClick={() => window.location.reload()}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        }
      />
      <div className="sr-page-body">
        {decisionError && (
          <div className="sr-empty" role="alert">
            {decisionError}
          </div>
        )}
        <QueueTable
          events={items.map((raw, i) => {
            const a = raw as Record<string, unknown>;
            const id = typeof a.id === "string" ? a.id : `appr-${i}`;
            const ts = firstString(a, ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"]) ?? "";
            const agent = firstString(a, ["agent"]) ?? "";
            const repo = firstString(a, ["repo", "project"]) ?? "";
            const who = [agent, repo].filter(Boolean).join(" · ") || undefined;
            const rule = firstString(a, ["rule", "ruleId"]);
            const cmd = firstString(a, ["command", "cmd"]);
            return {
              id,
              ts,
              severity: "ask" as const,
              title: i === selected ? "▸ Approval requested" : "Approval requested",
              who,
              rule,
              cmd,
            };
          })}
          emptyMessage="No pending approvals — you're clear"
          rowAction={(e) => (
            <div className="sr-approval-actions">
              <button type="button" onClick={() => void decide(e.id, "approve", setDecisionError)}>
                A
              </button>
              <button type="button" onClick={() => void decide(e.id, "deny", setDecisionError)}>
                D
              </button>
            </div>
          )}
        />
        <div className="sr-keyhints">Keyboard: j/k navigate · a approve · d deny</div>
      </div>
    </>
  );
}
